// PORTED VERBATIM FROM apps/web-employer/src/components/employer/invite-applicant-dialog.tsx
// Keep in sync — the component is generic over orgId, so admin (concierge) and
// employer share identical behavior. Admin passes the posting's org id.
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  FormField,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  useToast,
} from "@chotca/ui";
import {
  useCreateInvite,
  useCreateSlot,
  useOrgAddresses,
  useSlotsForLocation,
} from "@chotca/api-client";
import type { InterviewSlot } from "@chotca/sdk";
import { SLOT_STEP_SECONDS } from "@chotca/config";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

const MAX_PICK = 5;
const WEEKDAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"] as const;

/**
 * Employer-facing modal: "Mời ứng viên phỏng vấn".
 *
 * UX model:
 *   - One weekly calendar (Mon–Sun) per location.
 *   - Each day column shows existing future slots as pills + a "+" to add one.
 *   - Tap a pill to include it as an option for this invite (max 5).
 *   - Submit sends N proposed time options; worker picks 1.
 */
export function InviteApplicantDialog({
  open,
  onClose,
  onInvited,
  orgId,
  applicationId,
  workerName,
}: {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful invite so callers can refresh applicant lists. */
  onInvited?: () => void;
  orgId: string;
  applicationId: string;
  workerName: string;
}) {
  const { success: toastSuccess, error: toastError } = useToast();
  const addressesQ = useOrgAddresses(orgId);
  const addresses = useMemo(
    () => (addressesQ.data?.items ?? []).filter((a) => !a.archivedAt),
    [addressesQ.data],
  );

  const [locationId, setLocationId] = useState<string>("");
  const effectiveLocationId = locationId || addresses[0]?.id || "";
  const selectedAddress = useMemo(
    () => addresses.find((a) => a.id === effectiveLocationId) ?? null,
    [addresses, effectiveLocationId],
  );

  const slotsQ = useSlotsForLocation(orgId, effectiveLocationId);
  const slots = useMemo(() => slotsQ.data?.items ?? [], [slotsQ.data]);

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [weekStart, setWeekStart] = useState<Date>(() =>
    startOfWeek(new Date()),
  );
  const [creatingFor, setCreatingFor] = useState<string | null>(null); // ISO date key (yyyy-mm-dd) of the day being edited

  // Reset state when modal closes so the next open starts fresh.
  useEffect(() => {
    if (!open) {
      setPicked(new Set());
      setCreatingFor(null);
      setLocationId("");
      setWeekStart(startOfWeek(new Date()));
    }
  }, [open]);

  // Slots are scoped per location — switching location must clear picks.
  useEffect(() => {
    setPicked(new Set());
    setCreatingFor(null);
  }, [effectiveLocationId]);

  const createSlot = useCreateSlot(orgId);
  const createInvite = useCreateInvite(orgId);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const slotsByDay = useMemo(() => {
    const now = Date.now();
    const map = new Map<string, InterviewSlot[]>();
    for (const s of slots) {
      const start = new Date(s.startAt);
      if (start.getTime() < now) continue; // hide past slots
      const key = dayKey(start);
      const arr = map.get(key);
      if (arr) arr.push(s);
      else map.set(key, [s]);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [slots]);

  function togglePick(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_PICK) next.add(id);
      return next;
    });
  }

  async function handleCreateSlot(
    dayISO: string,
    time: string,
    capacity: number,
  ) {
    if (!effectiveLocationId || !time) return;
    const [hhStr, mmStr] = time.split(":");
    const [yyStr, moStr, ddStr] = dayISO.split("-");
    const startAt = new Date(
      Number(yyStr),
      Number(moStr) - 1,
      Number(ddStr),
      Number(hhStr),
      Number(mmStr),
      0,
      0,
    );
    if (startAt.getTime() < Date.now()) {
      toastError("Khung giờ đã qua");
      return;
    }
    try {
      const created = await createSlot.mutateAsync({
        orgAddressId: effectiveLocationId,
        startAt: startAt.toISOString(),
        capacityTotal: capacity,
      });
      setPicked((prev) => {
        const next = new Set(prev);
        if (next.size < MAX_PICK) next.add(created.id);
        return next;
      });
      setCreatingFor(null);
      toastSuccess("Đã tạo khung giờ");
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Tạo khung giờ thất bại");
    }
  }

  async function onSubmit() {
    if (picked.size === 0 || !effectiveLocationId) return;
    try {
      await createInvite.mutateAsync({
        applicationId,
        orgAddressId: effectiveLocationId,
        slotIds: Array.from(picked),
      });
      toastSuccess(`Đã gửi lời mời tới ${workerName}`);
      onInvited?.();
      onClose();
    } catch (e) {
      toastError(e instanceof Error ? e.message : "Mời PV thất bại");
    }
  }

  const todayKey = dayKey(new Date());
  const canGoPrev = weekStart.getTime() > startOfWeek(new Date()).getTime();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Mời ${workerName} phỏng vấn`}
      description="Đề xuất các khung giờ — ứng viên sẽ chọn 1 để xác nhận."
      size="lg"
      footer={
        <>
          <div className="mr-auto text-sm text-ash">
            {picked.size === 0
              ? "Chưa chọn khung giờ nào"
              : `Đã chọn ${picked.size}/${MAX_PICK} khung giờ`}
          </div>
          <Button variant="ghost" onClick={onClose}>
            Hủy
          </Button>
          <Button
            onClick={onSubmit}
            disabled={
              picked.size === 0 ||
              !effectiveLocationId ||
              createInvite.isPending
            }
          >
            Gửi {picked.size > 0 ? `${picked.size} lựa chọn` : "lời mời"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {addresses.length === 0 && !addressesQ.isLoading ? (
          <div className="rounded-lg border border-smoke bg-mist/40 px-3 py-2 text-sm text-ash">
            Doanh nghiệp chưa có chi nhánh — thêm chi nhánh ở phần Cài đặt trước
            khi mời phỏng vấn.
          </div>
        ) : addresses.length > 1 ? (
          <FormField label="Chi nhánh phỏng vấn">
            <Select
              value={effectiveLocationId}
              onValueChange={setLocationId}
              options={addresses.map((a) => ({
                value: a.id,
                label: `${a.alias} — ${a.district}, ${a.city}`,
              }))}
            />
            {selectedAddress ? (
              <p className="mt-1 text-xs text-ash">{selectedAddress.address}</p>
            ) : null}
          </FormField>
        ) : selectedAddress ? (
          <div className="rounded-lg border border-smoke bg-mist/40 px-3 py-2">
            <p className="text-xs font-semibold text-ash">
              Chi nhánh phỏng vấn
            </p>
            <p className="text-sm font-semibold text-ink">
              {selectedAddress.alias}
            </p>
            <p className="text-xs text-ash">
              {selectedAddress.address} — {selectedAddress.district},{" "}
              {selectedAddress.city}
            </p>
          </div>
        ) : null}

        {/* Week navigation */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setWeekStart((d) => addDays(d, -7))}
            disabled={!canGoPrev}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-smoke text-ink transition-colors hover:bg-mist disabled:opacity-40"
            aria-label="Tuần trước"
          >
            <ChevronLeft size={16} />
          </button>
          <div className="text-sm font-semibold text-ink">
            Tuần {fmtDayMonth(days[0]!)} – {fmtDayMonth(days[6]!)}
          </div>
          <button
            type="button"
            onClick={() => setWeekStart((d) => addDays(d, 7))}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-smoke text-ink transition-colors hover:bg-mist"
            aria-label="Tuần sau"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="overflow-x-auto">
          <div className="grid min-w-[640px] grid-cols-7 gap-2">
            {days.map((day, idx) => {
              const key = dayKey(day);
              const daySlots = slotsByDay.get(key) ?? [];
              const isToday = key === todayKey;
              const isPast = day.getTime() < new Date().setHours(0, 0, 0, 0);
              return (
                <div key={key} className="flex min-w-0 flex-col gap-2">
                  <div
                    className={`flex items-baseline justify-between rounded-lg px-2 py-1 ${
                      isToday ? "bg-lime/15" : ""
                    }`}
                  >
                    <span className="text-xs font-semibold text-ash">
                      {WEEKDAY_LABELS[idx]}
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        isToday ? "text-ink" : "text-ink"
                      }`}
                    >
                      {day.getDate()}
                    </span>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    {daySlots.map((s) => {
                      const full = s.capacityRemaining <= 0;
                      const isPicked = picked.has(s.id);
                      const reachedMax = !isPicked && picked.size >= MAX_PICK;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          disabled={full || reachedMax}
                          onClick={() => togglePick(s.id)}
                          className={`flex flex-col items-start rounded-lg border px-2 py-1.5 text-left transition-colors ${
                            isPicked
                              ? "border-lime bg-lime/20"
                              : full
                                ? "cursor-not-allowed border-smoke bg-mist/40 opacity-50"
                                : reachedMax
                                  ? "cursor-not-allowed border-smoke bg-paper opacity-40"
                                  : "border-smoke bg-paper hover:border-ink"
                          }`}
                          title={
                            full
                              ? "Khung giờ đã đầy"
                              : reachedMax
                                ? `Tối đa ${MAX_PICK} khung giờ`
                                : ""
                          }
                        >
                          <span className="text-sm font-bold text-ink">
                            {fmtTime(s.startAt)}
                          </span>
                          <span className="text-[11px] text-ash">
                            {full
                              ? "Đã đầy"
                              : `${s.capacityRemaining}/${s.capacityTotal}`}
                          </span>
                        </button>
                      );
                    })}

                    {!isPast ? (
                      <AddSlotButton
                        open={creatingFor === key}
                        anchorOnRight={idx >= 4}
                        onOpenChange={(next) =>
                          setCreatingFor(next ? key : null)
                        }
                        onSubmit={(time, cap) =>
                          handleCreateSlot(key, time, cap)
                        }
                        pending={createSlot.isPending}
                      />
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {slots.length === 0 && !slotsQ.isLoading ? (
          <p className="text-center text-sm text-ash">
            Chưa có khung giờ nào — bấm <strong>Thêm</strong> trên ngày bạn rảnh
            để tạo.
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function AddSlotButton({
  open,
  anchorOnRight,
  onOpenChange,
  onSubmit,
  pending,
}: {
  open: boolean;
  anchorOnRight: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (time: string, capacity: number) => void;
  pending: boolean;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex w-full items-center justify-center gap-1 rounded-lg border border-dashed px-2 py-1.5 text-xs transition-colors ${
            open
              ? "border-lime bg-lime/10 text-ink"
              : "border-smoke text-ash hover:border-ink hover:text-ink"
          }`}
        >
          <Plus size={12} />
          Thêm
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={anchorOnRight ? "end" : "start"}
        // Don't steal focus on open — let the employer tap the time field
        // themselves so the native picker opens on an explicit gesture.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <CreateSlotForm
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          pending={pending}
        />
      </PopoverContent>
    </Popover>
  );
}

function CreateSlotForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (time: string, capacity: number) => void;
  onCancel: () => void;
  pending: boolean;
}) {
  const [time, setTime] = useState("09:00");
  const [capacity, setCapacity] = useState(1);

  return (
    <>
      <p className="mb-2 text-xs font-semibold text-ink">Tạo khung giờ</p>

      <FormField label="Giờ bắt đầu">
        <Input
          type="time"
          step={SLOT_STEP_SECONDS}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="cursor-pointer"
          onClick={(e) => {
            // Desktop nicety: open the native picker on a click anywhere in
            // the field (not just the clock icon). On iOS the picker already
            // opens on tap now that the input lives inside a focusable Popover
            // layer; showPicker throwing here is harmless and ignored.
            try {
              (
                e.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                }
              ).showPicker?.();
            } catch {
              /* unsupported / not allowed — native tap handles it */
            }
          }}
        />
      </FormField>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs font-medium text-ash">Số chỗ phỏng vấn</span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCapacity((c) => Math.max(1, c - 1))}
            disabled={capacity <= 1}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-smoke text-sm disabled:opacity-40"
            aria-label="Giảm"
          >
            −
          </button>
          <span className="w-5 text-center text-sm font-bold">{capacity}</span>
          <button
            type="button"
            onClick={() => setCapacity((c) => Math.min(5, c + 1))}
            disabled={capacity >= 5}
            className="flex h-7 w-7 items-center justify-center rounded-full border border-smoke text-sm disabled:opacity-40"
            aria-label="Tăng"
          >
            +
          </button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button variant="ghost" onClick={onCancel} className="flex-1" size="sm">
          Hủy
        </Button>
        <Button
          onClick={() => onSubmit(time, capacity)}
          disabled={pending || !time}
          className="flex-1"
          size="sm"
        >
          Tạo
        </Button>
      </div>
    </>
  );
}

// ----------------------------------------------------------------------------
// Date helpers
// ----------------------------------------------------------------------------
function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // Monday = 1, Sunday = 0; shift so Monday is start of week.
  const dow = out.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  out.setDate(out.getDate() + diff);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fmtDayMonth(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
